import { recordMockAudit } from './audit-log'
import { mockDelay, generateId, storageKey } from '@/services/types'
import type { ApiResponse, PaginatedResponse } from '@/services/types'
import type {
  UserAccount,
  UserRole,
  UserAccountStatus,
} from '@/types/auth'
import { hasAdminRole } from '@/types/auth'
import type {
  UserLoginRecord,
  CreateUserDto,
  UpdateUserDto,
  UserListParams,
  UserStats,
  BatchImportResult,
  ResetPasswordResult,
} from '@/types/user'
import { loadFromStorage as loadOrgs } from './organization'

const USERS_STORAGE_KEY = storageKey('users')
const LOGIN_RECORDS_KEY = storageKey('login_records')

// =================== Storage Helpers ===================

/** mock 专用扩展:模拟密码字段(真实 API 口径为脱敏 passwordSet,不下发 hash) */
type MockUser = UserAccount & { passwordHash?: string }

function loadUsers(): MockUser[] {
  const raw = localStorage.getItem(USERS_STORAGE_KEY)
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      return parsed.map((u: MockUser) => ({
        ...u,
        createdAt: new Date(u.createdAt),
        lastLoginAt: u.lastLoginAt ? new Date(u.lastLoginAt) : undefined,
      }))
    } catch {
      // fallthrough
    }
  }
  return []
}

function saveUsers(users: MockUser[]) {
  localStorage.setItem(USERS_STORAGE_KEY, JSON.stringify(users))
}

function loadLoginRecords(): UserLoginRecord[] {
  const raw = localStorage.getItem(LOGIN_RECORDS_KEY)
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      return parsed.map((r: UserLoginRecord) => ({
        ...r,
        createdAt: new Date(r.createdAt),
      }))
    } catch {
      // fallthrough
    }
  }
  return []
}

function saveLoginRecords(records: UserLoginRecord[]) {
  localStorage.setItem(LOGIN_RECORDS_KEY, JSON.stringify(records))
}

// =================== Helper Functions ===================

/**
 * mock 写侧留痕：统一落到 audit_log 的 mock 存储（与文档/配置/首页/单位/角色同源），
 * action 用归一后的统一动作码（create/update/delete/status_change/role_assign/
 * password_reset/force_logout/batch_import），必须落在后端 AuditAction 的 code 集合内，
 * 否则 mock 字典下发不出该码、页面只能显示原始 code。
 */
function logUserOperation(payload: {
  operationType: string
  targetUserId?: string | null
  targetUserName?: string | null
  beforeValue?: string | null
  afterValue?: string | null
  reason?: string | null
}): void {
  recordMockAudit({
    module: 'user',
    action: payload.operationType,
    targetType: 'user',
    targetId: payload.targetUserId ?? null,
    targetName: payload.targetUserName ?? null,
    summary: payload.reason ?? null,
    beforePayload: payload.beforeValue ?? null,
    afterPayload: payload.afterValue ?? null,
  })
}

function generateNumericId(): string {
  return String(Date.now())
}

function generateTempPassword(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

function getOrgNameById(orgId: string | undefined): string | undefined {
  if (!orgId) return undefined
  const orgs = loadOrgs()
  const org = orgs.find((o: { id: string }) => o.id === orgId)
  return org?.name
}

function getOrgStatusById(orgId: string | undefined): string | undefined {
  if (!orgId) return undefined
  const orgs = loadOrgs()
  const org = orgs.find((o: { id: string }) => o.id === orgId)
  return org?.status
}

// =================== API Functions ===================

export async function listUsers(params: UserListParams): Promise<PaginatedResponse<UserAccount>> {
  await mockDelay(200)
  let items = loadUsers()

  if (params.keyword) {
    const kw = params.keyword.trim()
    items = items.filter(
      (u) => u.displayName.includes(kw) || u.mobile.includes(kw)
    )
  }
  if (params.role) items = items.filter((u) => u.role === params.role)
  if (params.status) items = items.filter((u) => (u.status || 'normal') === params.status)
  if (params.organizationId !== undefined) {
    if (params.organizationId === '') {
      items = items.filter((u) => !u.organizationId)
    } else {
      items = items.filter((u) => u.organizationId === params.organizationId)
    }
  }

  const total = items.length
  const page = params.page ?? 1
  const pageSize = params.pageSize ?? 10
  const start = (page - 1) * pageSize
  const paginated = items.slice(start, start + pageSize)

  return { items: paginated, total, page, pageSize }
}

export async function getUser(id: string): Promise<ApiResponse<UserAccount>> {
  await mockDelay(200)
  const items = loadUsers()
  const item = items.find((u) => u.id === id)
  if (!item) return { code: 400, message: '用户不存在' }
  return { code: 200, data: item }
}

export async function getUserStats(): Promise<ApiResponse<UserStats>> {
  await mockDelay(200)
  const items = loadUsers()
  const now = new Date()
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  const stats: UserStats = {
    totalCount: items.length,
    normalCount: items.filter((u) => (u.status || 'normal') === 'normal').length,
    disabledCount: items.filter((u) => u.status === 'disabled').length,
    adminCount: items.filter((u) => hasAdminRole(u.role)).length,
    leaderCount: items.filter((u) => u.role === 'leader').length,
    managerCount: items.filter((u) => u.role === 'manager').length,
    standardCount: items.filter((u) => u.role === 'standard').length,
    monthlyNewCount: items.filter((u) => u.createdAt >= thisMonthStart).length,
    unassignedCount: items.filter((u) => !u.organizationId).length,
    activeCount: items.filter((u) => u.lastLoginAt !== undefined && u.lastLoginAt >= thirtyDaysAgo).length,
  }
  return { code: 200, data: stats }
}

export async function createUser(dto: CreateUserDto): Promise<ApiResponse<UserAccount & { tempPassword: string }>> {
  await mockDelay(300)
  const items = loadUsers()

  if (items.some((u) => u.mobile === dto.mobile)) {
    return { code: 400, message: '手机号已被注册' }
  }

  const tempPassword = generateTempPassword()
  const now = new Date()
  const orgName = getOrgNameById(dto.organizationId)

  const user: MockUser = {
    id: generateNumericId(),
    mobile: dto.mobile,
    displayName: dto.displayName,
    role: dto.role,
    organizationId: dto.organizationId,
    organizationName: orgName,
    status: 'normal',
    passwordHash: `mock_hash_${tempPassword}`,
    isFirstLogin: true,
    forcePasswordChange: true,
    createdAt: now,
    lastLoginAt: now,
  }

  items.push(user)
  saveUsers(items)

  logUserOperation({
    operationType: 'create',
    targetUserId: user.id,
    targetUserName: user.displayName,
    afterValue: JSON.stringify({ mobile: user.mobile, role: user.role, organizationId: user.organizationId }),
  })

  return { code: 200, data: { ...user, tempPassword } }
}

export async function updateUser(id: string, dto: UpdateUserDto): Promise<ApiResponse<UserAccount>> {
  await mockDelay(300)
  const items = loadUsers()
  const idx = items.findIndex((u) => u.id === id)
  if (idx === -1) return { code: 400, message: '用户不存在' }

  if (dto.mobile && items.some((u) => u.id !== id && u.mobile === dto.mobile)) {
    return { code: 400, message: '手机号已被注册' }
  }

  const before = { ...items[idx] }
  const updates: Partial<UserAccount> = {}
  if (dto.displayName !== undefined) updates.displayName = dto.displayName
  if (dto.mobile !== undefined) updates.mobile = dto.mobile
  // 修复单位审查#1：单位归属不再是「编辑」可写字段，改动只经 changeOrg（同步成员行与配额）

  items[idx] = { ...items[idx], ...updates }
  saveUsers(items)

  logUserOperation({
    operationType: 'update',
    targetUserId: id,
    targetUserName: items[idx].displayName,
    beforeValue: JSON.stringify({ displayName: before.displayName, mobile: before.mobile, organizationId: before.organizationId }),
    afterValue: JSON.stringify({ displayName: items[idx].displayName, mobile: items[idx].mobile, organizationId: items[idx].organizationId }),
  })

  return { code: 200, data: items[idx] }
}

export async function deleteUser(id: string): Promise<ApiResponse<void>> {
  await mockDelay(300)
  const items = loadUsers()
  const idx = items.findIndex((u) => u.id === id)
  if (idx === -1) return { code: 400, message: '用户不存在' }

  // Check for associated data (mock: always allow for now, can be enhanced)
  const userName = items[idx].displayName
  items.splice(idx, 1)
  saveUsers(items)

  logUserOperation({
    operationType: 'delete',
    targetUserId: id,
    targetUserName: userName,
  })

  return { code: 200 }
}

export async function changeUserStatus(
  id: string,
  status: UserAccountStatus,
  reason: string
): Promise<ApiResponse<UserAccount>> {
  await mockDelay(300)
  const items = loadUsers()
  const idx = items.findIndex((u) => u.id === id)
  if (idx === -1) return { code: 400, message: '用户不存在' }

  if (hasAdminRole(items[idx].role) && status === 'disabled') {
    return { code: 400, message: '不可禁用管理员账号' }
  }

  const before = items[idx].status || 'normal'
  items[idx].status = status
  saveUsers(items)

  logUserOperation({
    operationType: 'status_change',
    targetUserId: id,
    targetUserName: items[idx].displayName,
    beforeValue: before,
    afterValue: status,
    reason,
  })

  return { code: 200, data: items[idx] }
}

export async function changeUserRole(
  id: string,
  role: UserRole,
  reason?: string
): Promise<ApiResponse<UserAccount>> {
  await mockDelay(300)
  const items = loadUsers()
  const idx = items.findIndex((u) => u.id === id)
  if (idx === -1) return { code: 400, message: '用户不存在' }

  const before = items[idx].role
  items[idx].role = role
  saveUsers(items)

  logUserOperation({
    operationType: 'role_assign',
    targetUserId: id,
    targetUserName: items[idx].displayName,
    beforeValue: before,
    afterValue: role,
    reason,
  })

  return { code: 200, data: items[idx] }
}

export async function resetUserPassword(
  id: string,
  forceChange = true
): Promise<ApiResponse<ResetPasswordResult>> {
  await mockDelay(300)
  const items = loadUsers()
  const idx = items.findIndex((u) => u.id === id)
  if (idx === -1) return { code: 400, message: '用户不存在' }

  const tempPassword = generateTempPassword()
  items[idx].passwordHash = `mock_hash_${tempPassword}`
  items[idx].forcePasswordChange = forceChange
  saveUsers(items)

  logUserOperation({
    operationType: 'password_reset',
    targetUserId: id,
    targetUserName: items[idx].displayName,
    afterValue: JSON.stringify({ forceChange }),
  })

  return { code: 200, data: { tempPassword, forceChange } }
}

export async function forceLogout(userId: string): Promise<ApiResponse<void>> {
  await mockDelay(200)
  const items = loadUsers()
  const user = items.find((u) => u.id === userId)
  if (!user) return { code: 400, message: '用户不存在' }

  logUserOperation({
    operationType: 'force_logout',
    targetUserId: userId,
    targetUserName: user.displayName,
  })

  return { code: 200 }
}

export async function batchImportUsers(
  rows: any[],
  strategy: 'skip' | 'overwrite' | 'abort'
): Promise<ApiResponse<BatchImportResult>> {
  await mockDelay(500)
  const result: BatchImportResult = { total: rows.length, success: 0, failed: 0, errors: [] }
  const items = loadUsers()
  const created: UserAccount[] = []

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    if (!row.mobile || !row.displayName || !row.role) {
      result.failed++
      result.errors.push({ row: i + 1, reason: '必填字段缺失（手机号/姓名/角色）' })
      continue
    }
    if (!/^1[3-9]\d{9}$/.test(row.mobile)) {
      result.failed++
      result.errors.push({ row: i + 1, reason: '手机号格式错误' })
      continue
    }
    const existingIdx = items.findIndex((u) => u.mobile === row.mobile)
    if (existingIdx !== -1) {
      if (strategy === 'abort') {
        result.failed++
        result.errors.push({ row: i + 1, reason: `手机号 "${row.mobile}" 已存在` })
        continue
      } else if (strategy === 'skip') {
        continue
      } else if (strategy === 'overwrite') {
        const orgName = getOrgNameById(row.organizationId)
        items[existingIdx] = {
          ...items[existingIdx],
          displayName: row.displayName,
          role: row.role,
          organizationId: row.organizationId,
          organizationName: orgName,
          updatedAt: new Date(),
        } as any
        result.success++
        continue
      }
    }

    const tempPassword = generateTempPassword()
    const orgName = getOrgNameById(row.organizationId)
    const now = new Date()
    const user: MockUser = {
      id: generateNumericId(),
      mobile: row.mobile,
      displayName: row.displayName,
      role: row.role,
      organizationId: row.organizationId,
      organizationName: orgName,
      status: 'normal',
      passwordHash: `mock_hash_${tempPassword}`,
      isFirstLogin: true,
      forcePasswordChange: true,
      createdAt: now,
      lastLoginAt: now,
    }
    created.push(user)
    items.push(user)
    result.success++
  }

  if (strategy === 'abort' && result.errors.length > 0) {
    return { code: 400, message: '导入存在冲突，已终止', data: result }
  }

  saveUsers(items)
  for (const user of created) {
    logUserOperation({
      operationType: 'batch_import',
      targetUserId: user.id,
      targetUserName: user.displayName,
      afterValue: JSON.stringify({ mobile: user.mobile, role: user.role }),
    })
  }

  return { code: 200, data: result }
}

export async function exportUsers(params: UserListParams): Promise<Blob> {
  await mockDelay(300)
  const res = await listUsers({ ...params, page: 1, pageSize: 10000 })
  const rows = res.items.map((u: UserAccount) => ({
    姓名: u.displayName,
    手机号: u.mobile,
    角色: u.role,
    所属单位: u.organizationName || '未分配',
    状态: u.status || 'normal',
    创建时间: u.createdAt.toLocaleString('zh-CN'),
  }))
  const XLSX = await import('xlsx')
  const ws = XLSX.utils.json_to_sheet(rows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, '用户列表')
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

// =================== Login Records ===================

export async function listLoginRecords(
  userId: string,
  page = 1,
  pageSize = 20
): Promise<PaginatedResponse<UserLoginRecord>> {
  await mockDelay(200)
  let items = loadLoginRecords().filter((r) => r.userId === userId)
  items.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

  const total = items.length
  const start = (page - 1) * pageSize
  return { items: items.slice(start, start + pageSize), total, page, pageSize }
}

export function recordLoginAttempt(
  userId: string,
  loginMethod: string,
  ipAddress: string,
  deviceInfo: string,
  success: boolean
) {
  const records = loadLoginRecords()
  records.push({
    id: generateId(),
    userId,
    loginMethod: loginMethod as any,
    ipAddress,
    deviceInfo,
    success,
    createdAt: new Date(),
  })
  // Keep last 50 per user
  const userRecords = records.filter((r) => r.userId === userId)
  if (userRecords.length > 50) {
    const toRemove = userRecords.length - 50
    let removed = 0
    for (let i = records.length - 1; i >= 0 && removed < toRemove; i--) {
      if (records[i].userId === userId) {
        records.splice(i, 1)
        removed++
      }
    }
  }
  saveLoginRecords(records)
}

// =================== User Status Check ===================

export function getUserStatusCheck(userId: string): { disabled: boolean; reason?: string } {
  const items = loadUsers()
  const user = items.find((u) => u.id === userId)
  if (!user) return { disabled: true, reason: '用户不存在' }
  if (user.status === 'disabled') return { disabled: true, reason: '账号已被禁用' }

  if (user.organizationId) {
    const orgStatus = getOrgStatusById(user.organizationId)
    if (orgStatus === 'disabled') return { disabled: true, reason: '您所在单位已被禁用' }
  }

  return { disabled: false }
}

export function getUserOrgRestriction(userId: string): { restricted: boolean; reason?: string } {
  const items = loadUsers()
  const user = items.find((u) => u.id === userId)
  if (!user || !user.organizationId) return { restricted: false }

  const orgStatus = getOrgStatusById(user.organizationId)
  if (orgStatus === 'overdue') return { restricted: true, reason: '您所在单位套餐已到期，功能受限' }

  return { restricted: false }
}
